import { Component, HostListener, OnInit } from '@angular/core';
import { MeasurementType } from '../../../model/measurement-type';
import { Tenant } from '../../../model/tenant';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslocoService } from '@ngneat/transloco';
import { AuthGuard } from '../../../common/auth-guard.service';
import { MeasurementTypesService } from '../../../services/measurement-types.service';
import { TenantsService } from '../../../services/tenants.service';
import { SelectItem } from 'primeng/api';
import { noSpecialCharacters } from '../../../shared/custom-validators';
import { MeasurementTypePersistRequest } from '../../../model/requests/measurement-type-persist-request';
import { DataType } from '../../../model/data-type';
import { CommonMessagesService } from "../../../services/common-messages.service";
import {ConversorsService} from "../../../services/conversors.service";

@Component({
  selector: 'app-measurement-types-form',
  templateUrl: './measurement-types-form.component.html',
  styleUrls: ['./measurement-types-form.component.scss']
})
export class MeasurementTypesFormComponent implements OnInit {

  public type: MeasurementType;

  public tenants: Tenant[];
  public dataTypes: SelectItem[];

  public typeForm: FormGroup;
  public persistingChanges: any;
  public formSuccess: boolean;

  public mode: 'create' | 'edit';
  public editInfo: {tenantUuid: string, oldSensor: string};

  public units: any[];
  public filteredUnits: any[];
  public convert = this.conversorsService.convert;

  constructor(private formBuilder: FormBuilder,
              private route: ActivatedRoute,
              private router: Router,
              private i18n: TranslocoService,
              private authGuard: AuthGuard,
              private measurementTypesService: MeasurementTypesService,
              private tenantsService: TenantsService,
              private commonMessagesService: CommonMessagesService,
              private conversorsService: ConversorsService) {
    this.dataTypes = [
      { label: 'forms.sensorType.STRING', value: DataType.STRING },
      { label: 'forms.sensorType.NUMBER', value: DataType.NUMBER },
      { label: 'forms.sensorType.BOOLEAN', value: DataType.BOOLEAN },
      { label: 'forms.sensorType.COORDINATES', value: DataType.COORDINATES },
      { label: 'forms.sensorType.ANALOG_SIGNAL', value: DataType.ANALOG_SIGNAL },
      { label: 'forms.sensorType.DIGITAL_SIGNAL', value: DataType.DIGITAL_SIGNAL }
    ];
  }

  ngOnInit(): void {
    this.route.url.subscribe(urlSegment => {
      if (urlSegment[0].path === 'new') {
        this.mode = 'create';
        this.tenantsService.getTenantSummaries().toPromise().then(response => {
          this.tenants = response.tenants;
          this.loadCreationForm();
        });
      } else {
        this.mode = 'edit';
        this.loadEditionForm(urlSegment[0].path, urlSegment[1].path);
      }
    });
  }

  loadCreationForm(): void {
    this.type = new MeasurementType();
    if (!this.authGuard.superAdminRequired()) {
      this.type.tenant = this.tenants[0];
    }
    this.createForm(this.type);
  }

  loadEditionForm(tenantUuid: string, sensor: string): void {
    this.tenantsService.getTenantMeasurementType(tenantUuid, sensor).toPromise().then(response => {
      this.type = response.measurementType;
      this.tenants = [ this.type.tenant ];
      this.editInfo = { tenantUuid: this.type.tenant.uuid, oldSensor: this.type.sensor };
      this.createForm(this.type);
    });
  }

  private createForm(type: MeasurementType): void {
    this.typeForm = this.formBuilder.group({
      sensor: [type.sensor, [Validators.required, Validators.maxLength(255), noSpecialCharacters]],
      unit: [type.unit, [Validators.maxLength(255)]],
      dataType: [type.dataType, [Validators.maxLength(255)]],
      tenant: [type.tenant, [Validators.required]]});
    this.typeForm.markAsPending();

    if (!this.authGuard.superAdminRequired()) {
      this.typeForm.get('tenant').disable();
    }

    if (this.mode === 'edit') {
      this.typeForm.get('tenant').disable();
    }
  }

  persistTypeData(): void {
    if (!this.typeForm.valid) { return; }

    this.persistingChanges = true;
    const persistData = this.buildPersistData();

    let promise;
    if (this.mode === 'create') {
      promise = this.measurementTypesService.createMeasurementType(persistData).toPromise();
    } else {
      promise = this.measurementTypesService.editMeasurementType(persistData).toPromise();
    }

    promise.then(response => {
      this.formSuccess = true;
      this.router.navigate([`/system/sensor-types`]);
    }).catch(error => {
      this.commonMessagesService.showPersistingError(this.mode, error, persistData.sensor);
      this.persistingChanges = false;
    });
  }

  private buildPersistData(): MeasurementTypePersistRequest {
    const formValue = this.typeForm.getRawValue();
    return {
      sensor: formValue.sensor,
      unit: formValue.unit,
      dataType: formValue.dataType,
      tenantUuid: this.mode === 'edit' ? this.editInfo.tenantUuid : formValue.tenant.uuid,
      oldSensor: this.mode === 'edit' ? this.editInfo.oldSensor : null
    };
  }

  filterUnit(event): void {
    const units = this.convert().possibilities();
    let filtered: any[] = [];
    let query = event.query;
    for (let i = 0; i < units.length; i++) {
      let unit = units[i];
      if (unit.toLowerCase().indexOf(query.toLowerCase()) == 0) {
        filtered.push(unit);
      }
    }
    this.filteredUnits = filtered;
  }

  @HostListener('window:beforeunload')
  canDeactivate(): boolean {
    return this.formSuccess || this.typeForm.untouched;
  }

}
